import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "cxfcomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "cxfcomponents/Navbars/DashboardNavbar";
import StatusBadge from "cxfcomponents/StatusBadge";
import useSlugSchedule from "hooks/useSlugSchedule";
import useAllowScheduleActions from "hooks/useAllowScheduleActions";
import ScheduleImg from "assets/images/schedule-details.svg";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import SchedulerDialog from "layouts/Flow Designer/components/ExecutionsCalendar/components/SchedulerDialog";
import ScheduleTabs from "../ScheduleTabs";
import { useResourceListener } from "hooks/useResourceListener";

const ScheduleInfo = () => {
   const [openSchedulerDialog, setOpenSchedulerDialog] = useState(false);
   const [selectedEvent, setSelectedEvent] = useState(null);
   const { schedule, reloadSchedules } = useSlugSchedule();
   const permissions = useAllowScheduleActions(schedule);
   const navigate = useNavigate();
   useResourceListener("schedulesSharingUpdate", reloadSchedules);

   const backToScheduleTable = () => {
      navigate("/workspace/schedules");
   };

   const convertHtmlToString = (html) => {
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.firstChild?.textContent || "";
   };

   const handleCloseSchedulerDialog = () => {
      setOpenSchedulerDialog(false);
      setSelectedEvent(null);
   };

   const handleEdit = () => {
      setSelectedEvent(schedule);
      setOpenSchedulerDialog(true);
   };

   if (!schedule) {
      navigate("/workspace/schedules");
      return null;
   }

   return (
      <>
         <div
            style={{
               padding: "8px",
               position: "sticky",
               top: 0,
               zIndex: 1000,
               backgroundColor: "var(--main-bg-color)"
            }}
         >
            <DashboardNavbar title={schedule?.title ?? ""} />
         </div>
         <DashboardLayout
            sx={{
               display: "flex",
               flexDirection: "column",
               minHeight: "100vh",
               height: "100vh",
               boxSizing: "border-box",
               overflow: "hidden"
            }}
         >
            <SoftBox
               mt={-3}
               sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0
               }}
            >
               <Card
                  className="schedule"
                  sx={{
                     backgroundColor: "transparent !important",
                     overflow: "hidden",
                     boxShadow: "none !important",
                     display: "flex",
                     flexDirection: "column",
                     flex: 1,
                     minHeight: 0
                  }}
               >
                  <Card
                     sx={{
                        overflow: "visible",
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0
                     }}
                  >
                     {/* Top Part */}
                     <SoftBox
                        alignItems="flex-start"
                        justifyContent="space-between"
                        sx={{
                           display: {
                              sm: "block",
                              lg: "flex"
                           },
                           padding: {
                              sm: "12px 32px 20px 32px",
                              lg: "48px 64px 46px 64px"
                           },
                           flexWrap: {
                              sm: "wrap",
                              lg: "noWrap"
                           },
                           columnGap: "40px",
                           height: "max-content"
                        }}
                     >
                        <SoftBox display="flex" alignItems="flex-start" sx={{ marginBottom: { sm: "16px", lg: "0" }, position: "relative" }}>
                           {/* Go Back Button just above the schedule logo */}
                           <SoftBox sx={{ position: "absolute", top: "-32px", left: 0 }}>
                              <button
                                 onClick={backToScheduleTable}
                                 style={{
                                    alignItems: "center",
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    display: "flex",
                                    maxWidth: "max-content",
                                    fontSize: "14px",
                                    padding: 0
                                 }}
                              >
                                 <SoftBox
                                    sx={{
                                       display: "grid",
                                       marginRight: "16px",
                                       placeContent: "center",
                                       fontSize: "28px",
                                       fontWeight: "300"
                                    }}
                                 >
                                    <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                       <path d="M8.77783 0.5L0.999657 8.27817L8.77783 16.0563" stroke="#7B809A" />
                                    </svg>
                                 </SoftBox>
                                 <SoftTypography sx={{ fontWeight: "normal", fontSize: "14px" }}>Go back</SoftTypography>
                              </button>
                           </SoftBox>
                           <SoftBox
                              component="img"
                              borderRadius="16px"
                              marginRight="40px"
                              sx={{
                                 objectFit: "fill",
                                 width: {
                                    sm: "120px",
                                    lg: "174px"
                                 },
                                 minHeight: {
                                    sm: "120px",
                                    lg: "164px"
                                 },
                                 maxHeight: {
                                    sm: "120px",
                                    lg: "164px"
                                 },
                                 border: "0.5px solid #e6e6e6"
                              }}
                              src={ScheduleImg}
                           />
                           <SoftBox
                              sx={{
                                 maxWidth: {
                                    lg: "clamp(100px, 32vw, 540px)"
                                 },
                                 display: "flex",
                                 flexDirection: "column",
                                 minHeight: "164px"
                              }}
                           >
                              <SoftTypography fontSize="24px" fontWeight="bold" lineHeight="1.25" marginBottom="8px" className="white-to-dark">
                                 {schedule.title}
                              </SoftTypography>
                              <StatusBadge status={schedule.status} />
                              <SoftTypography sx={{ margin: "auto 0", fontSize: "13px" }}>{schedule.description ? convertHtmlToString(schedule.description) : "No description"}</SoftTypography>
                              <SoftBox display="flex" sx={{ marginTop: "auto" }}>
                                 {permissions.allowEdit && (
                                    <SoftButton
                                       sx={{
                                          margin: "0 20px 0 0",
                                          padding: "12px !important"
                                       }}
                                       variant="gradient"
                                       size="medium"
                                       color="primary"
                                       onClick={handleEdit}
                                    >
                                       <Icon sx={{ fontSize: "500px !important", marginRight: "8px" }}>edit</Icon>
                                       properties
                                    </SoftButton>
                                 )}
                              </SoftBox>
                           </SoftBox>
                        </SoftBox>
                        <SoftBox
                           sx={{
                              flexBasis: {
                                 lg: "max-content"
                              },
                              flexShrink: 0
                           }}
                        >
                           <SoftTypography sx={{ display: "flex", alignItems: "flexStart" }} className="white-to-dark" fontSize="12px" marginBottom="4px">
                              <span style={{ opacity: 0.6, width: "76px", display: "inline-block" }}>Date Created:</span>
                              <span
                                 style={{
                                    marginLeft: "10px",
                                    maxWidth: "100px",
                                    whiteSpace: "break-spaces"
                                 }}
                              >
                                 {formatDateAndTime(getLocalDateTimeString(schedule.created_date_time)) || ""}
                              </span>
                           </SoftTypography>
                           <SoftTypography sx={{ display: "flex", alignItems: "flexStart" }} className="white-to-dark" fontSize="12px" marginBottom="4px">
                              <span style={{ opacity: 0.6, width: "76px", display: "inline-block" }}>Last Edited:</span>
                              <span
                                 style={{
                                    marginLeft: "10px",
                                    maxWidth: "100px",
                                    whiteSpace: "break-spaces"
                                 }}
                              >
                                 {(schedule.updated_date_time && formatDateAndTime(getLocalDateTimeString(schedule.updated_date_time))) || ""}
                              </span>
                           </SoftTypography>
                        </SoftBox>
                     </SoftBox>
                     {/* End of Top Part */}
                     <SoftBox sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                        <ScheduleTabs />
                     </SoftBox>
                  </Card>
               </Card>
            </SoftBox>
            <SchedulerDialog open={openSchedulerDialog} selectedEvent={selectedEvent} closeDialog={handleCloseSchedulerDialog} />
         </DashboardLayout>
      </>
   );
};

export default ScheduleInfo;
